<template>
    <div 
      class="tw-px-3 tw-py-2.5 hover:tw-bg-gray-50 tw-transition-colors tw-cursor-pointer"
      :class="{ 'tw-bg-primary-50 hover:tw-bg-primary-100': notification.unread }"
      @click="openNotificationDetails"
    >
      <div class="tw-flex tw-items-center tw-gap-2">
        
        <!-- Icon -->
        <div class="tw-relative tw-w-12 tw-h-12">
             <div class="tw-w-full tw-h-full tw-flex tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-gray-100 tw-overflow-hidden tw-border tw-border-solid tw-border-gray-200">
               <img :src="$user.id === notification.data.sender.id ? notification.data.receiver.profile_image : notification.data.sender.profile_image" class="tw-w-full tw-h-full" />
                 <!-- <icon icon="solar:info-circle-bold" class="tw-text-blue-500 tw-text-sm" /> -->
                </div>
                <!-- Show different icon based on completion status -->
                <div  class="tw-absolute tw-bottom-0 tw-right-0 tw-rounded-full  tw-w-4 tw-h-4 tw-bg-white tw-flex tw-items-center tw-justify-center tw-border tw-border-white tw-border-solid">
                    <icon icon="solar:close-circle-bold" class="tw-text-rose-500 tw-text-2xl" />
                </div>
         </div>
        
        <!-- Content -->
        <div class="tw-flex-1 tw-min-w-0">
          <div class="tw-flex tw-items-start tw-justify-between">
            <p class="tw-text-sm tw-font-medium tw-text-gray-800 tw-truncate">{{ notification.title }}</p>
            <span class="tw-text-xs tw-text-gray-400 tw-whitespace-nowrap tw-ml-1">{{ formatTime(notification.created_at || notification.time) }}</span>
          </div>
          <p class="tw-text-xs tw-text-gray-500 tw-mt-0.5 tw-line-clamp-1">{{ notification.message }}</p>
        </div>
      </div>
    </div>
  </template>
  
  <script setup>
  import { defineProps, defineEmits } from 'vue';
  import { useI18n } from "vue-i18n";
  
  const { t } = useI18n();
  
  // Define props
  const props = defineProps({
    notification: {
      type: Object,
      required: true
    }
  });
  
  // Define emits
  const emit = defineEmits(['notificationRead']);
  
  // Open notification details and mark as read
  function openNotificationDetails() {
    console.log(`ConnectionRequestNotification: openNotificationDetails called for notification ${props.notification.id}`);
    // Emit event to mark as read
    if (props.notification.unread) {
      console.log(`ConnectionRequestNotification: Emitting notificationRead for notification ${props.notification.id}`);
      emit('notificationRead', props.notification.id);
    } else {
      console.log(`ConnectionRequestNotification: Notification ${props.notification.id} is already read`);
    }
    
    // Handle connection request specific actions
    if (props.notification.data) {
      handleNotificationAction(props.notification);
    }
  }
  
  // Handle notification action for connection requests
  function handleNotificationAction(notification) {
    console.log('ConnectionRequestNotification: handleNotificationAction called for notification', notification);
    // Add connection request specific handling here if needed
    // For example: navigate to connections page, show connection details, etc.
  }
  
  // Format timestamp to relative time (e.g. "2h ago")
  const formatTime = (timestamp) => {
    const now = new Date();
    let date;
    
    // Handle different timestamp formats
    if (typeof timestamp === 'string') {
      // ISO string format from API: "2025-04-26T23:35:51.000000Z"
      date = new Date(timestamp);
    } else if (typeof timestamp === 'number') {
      // Unix timestamp (seconds)
      date = new Date(timestamp * 1000);
    } else if (timestamp instanceof Date) {
      // Already a Date object
      date = timestamp;
    } else if (timestamp?.data?.timestamp) {
      // Handle notification.data.timestamp format
      date = new Date(timestamp.data.timestamp * 1000);
    } else {
      // Fallback to created_at if timestamp is undefined
      date = timestamp?.created_at ? new Date(timestamp.created_at) : new Date();
    }
    
    // Invalid date, use current time
    if (isNaN(date.getTime())) {
      date = new Date();
    }
    
    const diff = now - date;
    
    // Convert to minutes
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 60) {
      return minutes <= 0 
        ? t('time.just-now', 'Just now')
        : t('time.min-ago', { count: minutes }, `${minutes}m`);
    }
    
    // Convert to hours
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return t('time.hour-ago', { count: hours }, `${hours}h`);
    }
    
    // Convert to days
    const days = Math.floor(hours / 24);
    return t('time.day-ago', { count: days }, `${days}d`);
  };
  </script> 